import { useState } from 'react';
import { ChevronDown, Info, type LucideIcon } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../../ui/tooltip';
import { useMediaQuery } from '../../../hooks/useMediaQuery';
import { cn } from '../../../lib/utils';

interface CautionBannerProps {
    icon: LucideIcon;
    title: string;
    description?: React.ReactNode;
    tooltip?: string;
    referenceBlock?: {
        label: string;
        content: string;
    };
    children?: React.ReactNode;
}

export default function CautionBanner({ icon: Icon, title, description, tooltip, referenceBlock, children }: CautionBannerProps) {
    const isMobile = useMediaQuery('(max-width: 768px)');
    const [refExpanded, setRefExpanded] = useState(false);

    const collapsible = isMobile && !!referenceBlock;
    const showRefBlock = referenceBlock && (!isMobile || refExpanded);

    return (
        <div className="rounded-md border border-warning/25 overflow-hidden">
            <div className="bg-warning/15 px-3 py-1.5 md:px-4 md:py-2">
                <div className="flex items-center gap-2 text-sm font-medium text-warning-foreground">
                    <Icon className="h-4 w-4 shrink-0 text-warning" />
                    {title}
                </div>
            </div>
            {description && (
                <div
                    className={cn(
                        'bg-warning/5 px-3 py-1.5 md:px-4 md:py-2 text-sm text-white flex items-start gap-2',
                        collapsible && 'cursor-pointer',
                    )}
                    onClick={collapsible ? () => setRefExpanded(!refExpanded) : undefined}
                >
                    <span>{description}</span>
                    {tooltip && !isMobile && (
                        <TooltipProvider>
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <Info className="h-4 w-4 shrink-0 mt-0.5 cursor-help text-warning" />
                                </TooltipTrigger>
                                <TooltipContent side="top" className="max-w-xs text-xs">
                                    {tooltip}
                                </TooltipContent>
                            </Tooltip>
                        </TooltipProvider>
                    )}
                    {collapsible && (
                        <ChevronDown className={cn('h-4 w-4 shrink-0 mt-0.5 text-warning transition-transform', refExpanded && 'rotate-180')} />
                    )}
                </div>
            )}
            {showRefBlock && (
                <div className="bg-warning/8 px-3 py-2 md:px-4 md:py-3">
                    <div className="border-l-2 border-warning pl-2 md:pl-3">
                        <span className="text-xs uppercase tracking-wider font-medium text-warning-foreground/70">
                            {referenceBlock.label}
                        </span>
                        <p className="text-sm text-foreground mt-1 whitespace-pre-line">
                            {referenceBlock.content}
                        </p>
                    </div>
                </div>
            )}
            {children && (
                <div className="text-sm text-warning-foreground/80">
                    {children}
                </div>
            )}
        </div>
    );
}
